import React from 'react';
import { cn } from '@/lib/utils';
import Text from './Text';
import Button from './Button';

/**
 * Neutral "nothing here" block. Keeps every empty list on the same language
 * instead of a bespoke illustration per page — and always says what the vendor
 * can *do*, which is the part most empty states forget.
 */
export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  message?: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  compact?: boolean;
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  message,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  compact = false,
  className,
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center px-6',
      compact ? 'py-8' : 'py-14',
      className,
    )}
  >
    {icon ? (
      <div className="grid place-items-center w-14 h-14 rounded-pill bg-flup-surface-alt text-flup-ink-3 mb-4 [&_svg]:w-6 [&_svg]:h-6">
        {icon}
      </div>
    ) : null}

    <Text variant="h3" as="p">
      {title}
    </Text>

    {message ? (
      <Text variant="body" tone="secondary" as="p" className="mt-1.5 max-w-[360px]">
        {message}
      </Text>
    ) : null}

    {(actionLabel && onAction) || (secondaryLabel && onSecondary) ? (
      <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
        {actionLabel && onAction ? (
          <Button onClick={onAction}>{actionLabel}</Button>
        ) : null}
        {secondaryLabel && onSecondary ? (
          <Button variant="secondary" onClick={onSecondary}>
            {secondaryLabel}
          </Button>
        ) : null}
      </div>
    ) : null}
  </div>
);

export default React.memo(EmptyState);
